import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth, homeForRole } from "@/store/AuthContext";

export default function Login() {
  const { loginReal } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Preencha e-mail e senha.");
    setLoading(true);
    try {
      const user = await loginReal(email, password);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate(user.blocked ? "/assinar" : homeForRole(user.role), { replace: true });
    } catch (err) {
      toast.error("Não foi possível entrar", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="rounded-3xl border border-white/10 bg-white p-8 shadow-2xl">
          <div className="flex flex-col items-center text-center">
            <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-20 w-auto" />
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-200/70 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
              <ShieldCheck className="h-3 w-3" /> Acesso Restrito · LGPD & CFM Compliant
            </span>
            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Bem-vindo ao VeloClin</h1>
            <p className="mt-1 text-sm text-slate-500">Acesse o ecossistema inteligente da sua clínica</p>
          </div>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600">E-mail Profissional</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@clinica.com" className="border-slate-200 pl-9 focus-visible:ring-blue-600" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Senha</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="border-slate-200 pl-9 focus-visible:ring-blue-600" />
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/esqueci-senha" data-testid="login-forgot-link" className="text-xs font-semibold text-blue-600 hover:text-blue-700">Esqueci minha senha</Link>
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading} className="group flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(37,99,235,0.4)] transition-colors hover:bg-blue-700 disabled:opacity-60">
              {loading ? "Entrando..." : "Entrar no Sistema"}
              {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            Ainda não tem conta?{" "}
            <Link to="/cadastro" data-testid="login-to-register" className="font-bold text-blue-600 hover:text-blue-700">
              Teste grátis por 14 dias
            </Link>
          </p>
        </div>
        <p className="mt-5 text-center text-xs text-slate-400">© {new Date().getFullYear()} VeloClin · v1.0</p>
      </motion.div>
    </div>
  );
}
